import {graphql, compose} from 'react-apollo';
import {withHandlers} from 'recompose';

import {APPROVAL_MODE} from '../../../constants';
import {
  SAVE_TIME_BATCH_MUTATION,
  DELETE_ROW_MUTATION,
  COPY_PREVIOUS_WEEK_MUTATION
} from '../../queries';
import {buildTsInput} from '../../../shared/utils/queries';
import {
  applySaveTimeResultToCache,
  extractGraphQLErrorMessage
} from './_saveTimeHelpers';
import makeBlankRow from './_makeBlankRow';

// Flushes every queued action (cell edits, row deletes, and - if present -
// a pending Copy Last Week) accumulated while auto-save is OFF on a single
// user action.  Cell edits go out as one saveTimeBatch GraphQL round trip;
// row deletes are fired sequentially afterwards via deleteTimesheetRow.
// A queued copy runs FIRST (before cell saves and row deletes) because it
// replaces this week's entries wholesale - anything queued alongside it was
// already swept at queue time, so in practice the copy runs in isolation.
// This is the whole point of the batched-save feature: ten cells edited =
// ONE request to the server instead of ten, which matters for users on
// flaky/rate-limited ISP connections.
//
// Returns a promise that resolves when every item has been processed, whether
// individual items succeeded or failed.  Failed items are left in the queue
// so the user can retry by clicking "Save" again.
export default compose(
  graphql(DELETE_ROW_MUTATION, {name: 'deleteRowMutate'}),
  graphql(SAVE_TIME_BATCH_MUTATION, {name: 'saveTimeBatchMutate'}),
  graphql(COPY_PREVIOUS_WEEK_MUTATION, {name: 'copyPreviousWeekMutate'}),
  withHandlers({
    flushPendingSaves: ({
      setError,
      setSuccess,
      setSavePending,
      updateTimesheetData,
      clearPendingSave,
      timesheet,
      pendingSaves,
      mode,
      saveTimeBatchMutate,
      deleteRowMutate,
      copyPreviousWeekMutate
    }) => async () => {
      const queue = pendingSaves || {};
      const keys = Object.keys(queue);
      if (keys.length === 0) return;

      // Split the queue by item kind.  Existing cell-edit items have no
      // `kind` field (defaulting to 'save'); row-delete items carry
      // kind:'deleteRow' and are keyed by rowId; the optional Copy Last
      // Week item carries kind:'copyPreviousWeek' and is keyed by the
      // singleton COPY_PREVIOUS_WEEK_QUEUE_KEY.
      const rowDeletes = [];
      const cellSaves = [];
      let copyItem = null;
      keys.forEach(key => {
        const item = queue[key];
        if (!item) return;
        if (item.kind === 'copyPreviousWeek') copyItem = {key, ...item};
        else if (item.kind === 'deleteRow') rowDeletes.push({key, ...item});
        else cellSaves.push({key, ...item});
      });

      // Drop any cell-save items targeting a row that's also queued for
      // deletion - sending them would be wasted work (the row is about to
      // be gone) and could surface spurious Epicor errors.
      const deletingRowIds = new Set(rowDeletes.map(d => d.rowId));
      const effectiveSaves = cellSaves.filter(item => {
        const rowId = item.variables && item.variables.row && item.variables.row.id;
        return !rowId || !deletingRowIds.has(rowId);
      });
      // ...and clear the skipped ones from the queue right away so the
      // user's unsaved-changes count stays accurate.
      cellSaves
        .filter(item => !effectiveSaves.includes(item))
        .forEach(item => clearPendingSave(item.key));

      // Build a lock payload covering every affected date + entry id +
      // row id so users can't edit the same cells while the flush is in
      // flight.
      const lockPayload = {};
      effectiveSaves.forEach(item => {
        if (item.entryDate) lockPayload[item.entryDate] = true;
        if (item.variables && item.variables.entry && item.variables.entry.id) {
          lockPayload[item.variables.entry.id] = true;
        }
      });
      rowDeletes.forEach(item => {
        if (item.rowId) lockPayload[item.rowId] = true;
      });
      if (Object.keys(lockPayload).length) setSavePending(lockPayload);

      const unlock = () => {
        const unlockPayload = {};
        Object.keys(lockPayload).forEach(k => {
          unlockPayload[k] = false;
        });
        if (Object.keys(unlockPayload).length) setSavePending(unlockPayload);
      };

      let savedCellCount = 0;
      let deletedRowCount = 0;
      let copyRan = false;

      try {
        // Phase 0: queued Copy Last Week.  Runs before cell saves / row
        // deletes because a copy replaces this week's entries on the
        // server, making any follow-up edits index-ambiguous.  Queueing a
        // copy already sweeps out conflicting items, so this phase
        // normally runs in isolation.
        if (copyItem) {
          try {
            const {data} = await copyPreviousWeekMutate({
              variables: {
                ...buildTsInput(timesheet),
                includeTime: copyItem.includeTime
              },
              update(store) {
                // Mirror the cache fixup copyPreviousWeek.js does on the
                // immediate path: append a fresh blank row because the
                // timesheet id is the same and a blank row isn't added
                // automatically.
                updateTimesheetData(
                  {
                    rows: {
                      $push: [makeBlankRow(timesheet)]
                    }
                  },
                  store
                );
              }
            });
            const message =
              data &&
              data.copyPreviousWeek &&
              data.copyPreviousWeek.message;
            if (message) setSuccess(message.toString());
            clearPendingSave(copyItem.key);
            copyRan = true;
          } catch (err) {
            // Leave the queued copy in place so the user can retry.
            const message = extractGraphQLErrorMessage(err);
            setError(message || err);
          }
        }

        // Phase 1: batch cell saves.
        if (effectiveSaves.length > 0) {
          try {
            const items = effectiveSaves.map(({variables}) => ({
              row: variables.row,
              entry: variables.entry
            }));
            const {data} = await saveTimeBatchMutate({
              variables: {
                ...buildTsInput(timesheet),
                items,
                submit: mode === APPROVAL_MODE
              }
            });
            const results = (data && data.saveTimeBatch) || [];
            results.forEach((saveTime, i) => {
              if (!saveTime) return;
              const queued = effectiveSaves[i];
              const row = timesheet.rows[queued.rowIndex];
              if (!row) return;
              applySaveTimeResultToCache({
                updateTimesheetData,
                row,
                rowIndex: queued.rowIndex,
                entryIndex: queued.entryIndex,
                detailIndex: queued.detailIndex,
                saveTime
              });
              clearPendingSave(queued.key);
              savedCellCount += 1;
            });
          } catch (err) {
            // On a batch-level failure (network, schema, server error)
            // leave every queued edit in place so the user can retry.
            const message = extractGraphQLErrorMessage(err);
            setError(message || err);
          }
        }

        // Phase 2: sequentially fire queued row deletes.  Done AFTER
        // cell saves so any edits that happened to live on soon-to-be-
        // deleted rows can't race against the row-delete mutation.  We
        // track already-deleted ids so index math stays correct as the
        // live timesheet shrinks between iterations.
        const alreadyDeletedIds = new Set();
        for (const item of rowDeletes) {
          try {
            const {data} = await deleteRowMutate({
              variables: item.variables
            });
            if (!data || !data.deleteTimesheetRow) {
              setError('Delete failed');
              continue;
            }
            // Resolve the row's current index using the original row
            // array offset by any deletions we've already applied - the
            // splice below is what actually mutates the cache, so we
            // compute indices against the live (post-earlier-deletes)
            // timesheet rather than the in-memory snapshot.
            const liveRows = timesheet.rows.filter(
              r => !alreadyDeletedIds.has(r.id)
            );
            const idx = liveRows.findIndex(r => r.id === item.rowId);
            if (idx >= 0) {
              updateTimesheetData({rows: {$splice: [[idx, 1]]}});
            }
            alreadyDeletedIds.add(item.rowId);
            clearPendingSave(item.key);
            deletedRowCount += 1;
          } catch (err) {
            const message = extractGraphQLErrorMessage(err);
            setError(message || err);
            // Leave this item queued so the user can retry.
          }
        }
      } finally {
        unlock();
      }

      // Success messaging mirrors the copy used for single-save and
      // single-delete paths so the user gets familiar feedback.  When a
      // Copy Last Week flushed the server-returned message already went
      // through setSuccess above, so we only aggregate cell/row counts
      // here to avoid overwriting that with a less informative summary.
      if (!copyRan && (savedCellCount > 0 || deletedRowCount > 0)) {
        const bits = [];
        if (savedCellCount > 0) {
          bits.push(
            savedCellCount === 1
              ? '1 timesheet entry saved'
              : `${savedCellCount} timesheet entries saved`
          );
        }
        if (deletedRowCount > 0) {
          bits.push(
            deletedRowCount === 1
              ? '1 row deleted'
              : `${deletedRowCount} rows deleted`
          );
        }
        setSuccess(bits.join(', ') + '.');
      }
    }
  })
);
